import React, { useState, useEffect } from "react";
import {
  TextInput,
  PasswordInput,
  Button,
  Paper,
  Title,
  Text,
  Checkbox,
  Loader,
  Center,
  Container,
  Stack,
  Anchor,
} from "@mantine/core";
import { useParams, useNavigate } from "react-router-dom";
import { verifySignupToken, createAccount } from "../../api";
import Footer from "../Footer";

export const AccountSetup = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const { token } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await verifySignupToken(token);
        setIsValidToken(response.isValid);
      } catch (err) {
        setIsValidToken(false);
      } finally {
        setIsLoading(false);
      }
    };

    validateToken();
  }, [token]);

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loader />
      </div>
    );
  }

  if (!isValidToken) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {/* Background image div */}
        <div
          style={{
            backgroundImage: "url('/static/socius hero.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            height: "85vh",
            width: "100%",
            position: "fixed",
            top: "68px",
            left: "0px",
            zIndex: -1,
          }}
        />

        <Container
          size="lg"
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "4rem 1rem",
          }}
        >
          <Stack
            align="center"
            gap="xl"
            style={{
              position: "relative",
              width: "24rem",
            }}
          >
            <a href="/">
              <img
                src="/static/Socius_Full_DRK.svg"
                alt="Socius Legal"
                style={{
                  height: "auto",
                  width: "10rem",
                  marginBottom: "2rem",
                }}
              />
            </a>

            <Paper p="xl" radius="md" style={{ width: "100%" }}>
              <Title order={2} ta="center" mb="xl" ff="DM Sans">
                Invalid or Expired Link
              </Title>
              <Text ta="center" ff="DM Sans">
                The link you used is invalid, expired, or you have already
                signed up. Please request a new signup link.
              </Text>
            </Paper>
          </Stack>
        </Container>

        <footer
          style={{
            width: "100%",
            padding: "1rem",
            backgroundColor: "rgba(255, 255, 255, 0.3)",
            textAlign: "center",
          }}
        >
          <Text size="sm" c="dimmed" style={{ fontFamily: "DM Sans" }}>
            © Socius Legal Corporation 2024
          </Text>
        </footer>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) {
      setError("Please accept the User Agreement");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      const response = await createAccount(token, password, acceptedTerms);
      if (response.access_token) {
        navigate("/login");
      } else {
        setError("Failed to create account");
      }
    } catch (err) {
      if (err.response?.status === 409) {
        navigate("/login");
        return;
      }
      const errorMessage =
        err.response?.data?.detail || "An error occurred. Please try again.";
      setError(errorMessage);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Background image div */}
      <div
        style={{
          backgroundImage: "url('/static/socius hero.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          height: "85vh",
          width: "100%",
          position: "fixed",
          top: "68px",
          left: "0px",
          zIndex: -1,
        }}
      />

      <Container
        size="lg"
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "4rem 1rem",
        }}
      >
        <Stack
          align="center"
          gap="xl"
          style={{
            position: "relative",
            width: "24rem",
          }}
        >
          <a href="/">
            <img
              src="/static/Socius_Full_DRK.svg"
              alt="Socius Legal"
              style={{
                height: "auto",
                width: "10rem",
                marginBottom: "2rem",
              }}
            />
          </a>

          <Paper p="xl" radius="md" style={{ width: "100%" }}>
            <Title order={2} ta="center" mb="xl" ff="DM Sans">
              Set Up Your Account
            </Title>
            <form onSubmit={handleSubmit}>
              <Stack gap="md">
                <PasswordInput
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  styles={{ input: { textAlign: "center" } }}
                />
                <PasswordInput
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  styles={{ input: { textAlign: "center" } }}
                />
                <Checkbox
                  label={
                    <Text size="sm" ff="DM Sans">
                      I accept the{" "}
                      <Anchor
                        href="https://sociuslegal.com/static/policies.html"
                        target="_blank"
                        style={{ textDecoration: "underline" }}
                      >
                        User Agreement
                      </Anchor>
                    </Text>
                  }
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.currentTarget.checked)}
                />
                {error && (
                  <Text c="red" size="sm" ta="center">
                    {error}
                  </Text>
                )}
                <Button
                  type="submit"
                  fullWidth
                  style={{
                    padding: "0.25rem 1rem",
                    fontSize: "1rem",
                    fontWeight: "700",
                  }}
                >
                  Create Account
                </Button>
              </Stack>
            </form>
          </Paper>
        </Stack>
      </Container>

      <footer
        style={{
          width: "100%",
          padding: "1rem",
          backgroundColor: "rgba(255, 255, 255, 0.3)",
          textAlign: "center",
        }}
      >
        <Text size="sm" c="dimmed" style={{ fontFamily: "DM Sans" }}>
          © Socius Legal Corporation 2024
        </Text>
      </footer>
    </div>
  );
};
